import { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useToast } from '@/shared/toast';

export type InterventionCategory = 'Incendio' | 'Soccorso' | 'Incidente' | 'Altro' | null;

interface Props {
  open: boolean;
  category: InterventionCategory;
  onClose: () => void;
}

const NOTE_SUGGESTIONS: Record<string, string[]> = {
  Incendio: [
    'Incendio abitazione', 'Incendio veicolo', 'Incendio bosco/vegetazione',
    'Principio di incendio', 'Incendio rifiuti', 'Falso allarme',
  ],
  Soccorso: [
    'Soccorso a persona', 'Recupero animale', 'Apertura porta',
    'Assistenza sanitaria', 'Persona bloccata', 'Salvataggio in quota',
  ],
  Incidente: [
    'Incidente stradale', 'Incidente sul lavoro', 'Fuga di gas',
    'Allagamento', 'Crollo struttura', 'Messa in sicurezza',
  ],
  Altro: [
    'Bonifica', 'Servizio preventivo', 'Vigilanza', 'Sopralluogo',
    'Esercitazione', 'Assistenza evento', 'Imenotteri', 'Calamità naturale',
  ],
};

function pad(n: number): string { return String(n).padStart(2, '0'); }
function ymd(d: Date): string { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function hm(d: Date): string { return `${pad(d.getHours())}:${pad(d.getMinutes())}`; }

export function InterventionInsertModal({ open, category, onClose }: Props): React.JSX.Element | null {
  const [note, setNote] = useState('');
  // Default: data oggi, ora inizio = adesso, ora fine = adesso + 10 min
  const now = useMemo(() => new Date(), []);
  const tenMin = useMemo(() => new Date(now.getTime() + 10 * 60_000), [now]);
  const [date, setDate] = useState(ymd(now));
  const [startTime, setStartTime] = useState(hm(now));
  const [endTime, setEndTime] = useState(hm(tenMin));
  const toast = useToast();
  const qc = useQueryClient();

  const suggestions = useMemo(
    () => (category ? NOTE_SUGGESTIONS[category] ?? [] : ['Intervento generico']),
    [category],
  );

  const save = useMutation({
    mutationFn: async () => {
      await api.post('/interventions', {
        date: `${date}T00:00:00`,
        category: category ?? null,
        note: note.trim() || null,
        startTime: `${startTime}:00`,
        endTime: `${endTime}:00`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interventions.list'] });
      qc.invalidateQueries({ queryKey: ['interventions.stats.yearly'] });
      qc.invalidateQueries({ queryKey: ['interventions.stats.monthly'] });
      qc.invalidateQueries({ queryKey: ['dashboard.summary'] });
      toast.show('Intervento salvato', 'success');
      setNote('');
      onClose();
    },
    onError: () => toast.show('Errore nel salvataggio', 'error'),
  });

  if (!open) return null;

  const title = category ? `${categoryEmoji(category)} ${category}` : '➕ Inserisci Intervento';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Chiudi"
            style={{ background: 'transparent', border: 'none', fontSize: 22, color: '#999', cursor: 'pointer' }}>✕</button>
        </div>

        <div className="form-group">
          <label>Data</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Ora Inizio</label>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Ora Fine</label>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Suggerimenti rapidi</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map(s => (
              <button key={s} type="button"
                onClick={() => setNote(s)}
                style={{
                  background: '#eef2f7', border: '1px solid #d6dde6',
                  borderRadius: 16, padding: '4px 12px', fontSize: 12,
                  cursor: 'pointer', color: '#444',
                }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="note-area">Nota</label>
          <textarea id="note-area" rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Descrivi l'intervento..."
            style={{ width: '100%', padding: 12, border: '2px solid #e0e0e0', borderRadius: 8, fontFamily: 'inherit', fontSize: 14, resize: 'vertical' }} />
        </div>

        <div style={{
          marginTop: 18, paddingTop: 14, borderTop: '1px solid #ececec',
          display: 'flex', gap: 10, justifyContent: 'flex-end',
        }}>
          <button onClick={onClose} disabled={save.isPending}
            style={{ padding: '10px 22px', fontWeight: 600, fontSize: 14, border: 'none', borderRadius: 8, cursor: 'pointer',
                     background: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)', color: 'white',
                     boxShadow: '0 2px 6px rgba(108, 117, 125, 0.3)' }}>
            Annulla
          </button>
          <button onClick={() => save.mutate()} disabled={save.isPending}
            style={{ padding: '10px 24px', fontWeight: 700, fontSize: 14, border: 'none', borderRadius: 8, cursor: 'pointer',
                     background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white',
                     boxShadow: '0 2px 6px rgba(40, 167, 69, 0.3)' }}>
            {save.isPending ? 'Salvataggio…' : '💾 Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

function categoryEmoji(c: InterventionCategory): string {
  switch (c) {
    case 'Incendio': return '🔥';
    case 'Soccorso': return '🚑';
    case 'Incidente': return '🚗';
    case 'Altro': return '📝';
    default: return '';
  }
}
